import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users, HeartHandshake, Leaf, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AboutSection from "@/components/AboutSection";
import { Button } from "@/components/ui/button";

const PAGE_TITLE = "About Us | Aham Yoga Institute";
const PAGE_DESCRIPTION =
    "Learn about Aham Yoga Institute, our experienced instructors, holistic approach to wellness, and inclusive yoga community dedicated to helping you grow and transform.";

const PILLARS = [
    {
        title: "Experienced Instructors",
        body:
            "Our team of certified and passionate yoga instructors brings years of experience and a personalized approach to every session.",
        Icon: Users,
    },
    {
        title: "Holistic Approach",
        body:
            "We believe in nurturing the body, mind, and spirit. Our classes integrate physical postures, breathwork, and meditation to provide a complete wellness experience.",
        Icon: Leaf,
    },
    {
        title: "Inclusive Community",
        body:
            "At Aham Yoga Institute, we foster an inclusive and supportive community where everyone is welcome. Our space is open to individuals of all ages, backgrounds, and fitness levels.",
        Icon: HeartHandshake,
    },
];

export default function AboutPage({ onOpenAuth }) {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = PAGE_TITLE;
        const meta = document.querySelector('meta[name="description"]');
        const prevDesc = meta?.getAttribute("content") || "";
        if (meta) meta.setAttribute("content", PAGE_DESCRIPTION);
        return () => {
            document.title = prevTitle;
            if (meta) meta.setAttribute("content", prevDesc);
        };
    }, []);

    return (
        <div className="min-h-screen bg-paper" data-testid="about-page">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                {/* Migrated About section — reuses the exact homepage component so
                    text, image, formatting and design are guaranteed to stay
                    in sync. */}
                <AboutSection />

                {/* Why Choose Aham Yoga Institute? */}
                <section
                    className="py-20 md:py-28 bg-gradient-to-br from-brand-50/60 to-paper border-y border-brand-100/60"
                    data-testid="why-choose-section"
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold tracking-wide uppercase">
                                <Sparkles className="h-3.5 w-3.5" /> Why us
                            </span>
                            <h2 className="mt-5 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900 leading-tight">
                                Why choose <span className="text-brand-500">Aham</span>{" "}
                                <span className="text-accent-500">Yoga Institute</span>?
                            </h2>
                        </div>

                        <div
                            className="mt-12 grid md:grid-cols-3 gap-6"
                            data-testid="why-choose-cards"
                        >
                            {PILLARS.map(({ title, body, Icon }) => (
                                <article
                                    key={title}
                                    className="rounded-3xl bg-white border border-gray-100 p-7 shadow-soft hover:shadow-brand transition"
                                    data-testid={`why-choose-card-${title
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                >
                                    <span className="inline-flex h-12 w-12 rounded-2xl bg-brand-50 text-brand-700 items-center justify-center">
                                        <Icon className="h-6 w-6" strokeWidth={1.6} />
                                    </span>
                                    <h3 className="mt-5 font-heading text-xl font-semibold text-ink-900">
                                        {title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-ink-700">
                                        {body}
                                    </p>
                                </article>
                            ))}
                        </div>

                        {/* Join Us — closing inspirational block */}
                        <div
                            className="mt-14 rounded-3xl bg-ink-900 text-white p-10 md:p-14 relative overflow-hidden"
                            data-testid="join-us-block"
                        >
                            <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" />
                            <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-accent-500/20 blur-3xl pointer-events-none" />
                            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                                <div className="lg:col-span-8">
                                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-brand-200 text-xs font-semibold tracking-wide uppercase">
                                        Join us
                                    </span>
                                    <h3 className="mt-5 font-heading text-3xl md:text-4xl font-semibold leading-tight">
                                        Connect, grow, and transform with us.
                                    </h3>
                                    <p className="mt-5 text-base md:text-lg text-gray-300 leading-relaxed">
                                        Join us at Aham Yoga Institute and discover a space
                                        where you can connect, grow, and transform. Whether you
                                        are looking to enhance your physical health, find inner
                                        peace, or deepen your yoga practice, we are here to
                                        guide you every step of the way.
                                    </p>
                                </div>
                                <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
                                    <Button
                                        asChild
                                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6 py-5"
                                        data-testid="about-cta-courses"
                                    >
                                        <Link to="/courses">
                                            Explore Courses
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Link>
                                    </Button>
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-full border-white/20 bg-white/0 text-white hover:bg-white/10 hover:text-white px-6 py-5"
                                        data-testid="about-cta-contact"
                                    >
                                        <Link to="/#contact">Contact us</Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
